import axiosBase from "axios"
import {vxm} from "@/store"
import {Site, User, Notification, DebugNotification} from "@/interface"

const axios = axiosBase.create({
  baseURL     : "/api",
  responseType: "json"
})

export default class API {

  private static retry_count: number = 0

  private static async request(method: string, path: string, payload: any = {}): Promise<any> {
    let response
    if (vxm.auth.authData.IdToken) {
      axios.defaults.headers.Authorization = vxm.auth.authData.IdToken
    }
    if (vxm.auth.authData.AccessToken) {
      axios.defaults.headers.AccessToken = vxm.auth.authData.AccessToken
    }

    switch (method.toLowerCase()) {
      case "get":
        response = await axios.get(path, {params: payload}).catch(e => e.response)
        break
      case "post":
        response = await axios.post(path, payload).catch(e => e.response)
        break
      case "put":
        response = await axios.put(path, payload).catch(e => e.response)
        break
      case "delete":
        response = await axios.delete(path, {data: payload}).catch(e => e.response)
        break
      default:
        throw "undefined method"
    }

    if (response.data.status) {
      return response.data.data
    } else {
      if (
        (
          response.data.message === "The incoming token has expired" ||
          response.data.error.message === "Access Token has been revoked"
        ) &&
        API.retry_count === 0
      ) {
        API.retry_count = 1
        await vxm.auth.refresh()
        const response = await API.request(method, path, payload)
        API.retry_count = 0
        return response
      } else {
        throw response.data.error
      }
    }
  }

  static async test(payload: any) {
    return this.request("post", "/test", payload)
  }

  static async signup(payload: {
    email: string,
    password: string,
    given_name: string,
    family_name: string
  }): Promise<any> {
    return this.request("post", "/auth/signup", payload)
  }

  static async verify(payload: { sub: string, code: string }): Promise<any> {
    return this.request("post", "/auth/verify", payload)
  }

  static async signin(payload: { email: string, password: string }): Promise<any> {
    return this.request("post", "/auth/signin", payload)
  }

  static async signout(payload: any): Promise<any> {
    return this.request("post", "/auth/signout", payload)
  }

  static async refreshAuth(): Promise<any> {
    if (vxm.auth.authData.RefreshToken) {
      const payload = {refresh_token: vxm.auth.authData.RefreshToken}
      return this.request("post", "/auth/refresh", payload)
    } else {
      return null
    }
  }

  static async changePassword(payload: { proposed_password: string, previous_password: string }) {
    return this.request("post", "/change_password", payload)
  }

  static async resetPassword(payload: { email: string }) {
    return this.request("post", "/auth/reset_password", payload)
  }

  static async me(): Promise<any> {
    return this.request("get", "/me", {})
  }

  static async getUsers(): Promise<any> {
    return this.request("get", "/users", {})
  }

  static async updateUser(user: User) {
    return this.request("put", "/users/" + user.sub, user)
  }

  static async getSites(): Promise<any> {
    return this.request("get", "/sites", {})
  }

  static async createSite(site: Site) {
    return this.request("post", "/sites", site)
  }

  static async editSite(site: Site) {
    return this.request("put", "/sites/" + site.id, site)
  }

  static async getTokenSummaries(site_id: number): Promise<any> {
    return this.request("get", "/summary/tokens", {site_id: site_id})
  }

  static async getNotificationSummaries(params: { site_id: number, start_at?: number, end_at?: number, sort?: string, sort_direct?: string }): Promise<any> {
    return this.request("get", "/summary/notifications", params)
  }

  static async getNotifications(params: { page: number, filter?: object }): Promise<any> {

    const queries: any = params.filter || {}
    queries.site_id = vxm.app.selected_site_id

    return this.request("get", "/notifications", queries)
  }

  static async getNotification(params: { id: number }): Promise<any> {
    return this.request("get", "/notifications/" + params.id)
  }

  static async createNotification(notification: Notification) {
    notification.site_id = vxm.app.selected_site_id
    return this.request("post", "/notifications", notification)
  }

  static async cancelNotification(payload: { id: number }) {
    return this.request("delete", "/notifications/" + payload.id, {})
  }

  static async debugNotification(notification: DebugNotification) {
    return this.request("post", "/notifications/debug", notification)
  }

  static async ogp(payload: { url: string }) {
    return this.request("post", "/ogp", payload)
  }
}
